import { trackAiVoiceAuditionHistoryOpened } from '@src/analytics/EventTracker';
import { useStore } from '@src/stores-v2/StoreContext';
import { isAbortError } from '@src/utility/error';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { AuditionHistoryEntry } from '@src/repositories/DubbingRepository';

// Component-owned transient state for the workspace's audition history (DE12 /
// PLAT-10580). `DubbingStore` stays a pure catalog; history is fetched lazily
// when the History tab opens and prepended optimistically after a new audition.
// Abort-aware and re-scoped when the workspace changes.

/** History page size; "show more" grows the window by another page. */
export const AUDITION_HISTORY_PAGE_SIZE = 10;

export interface AuditionHistoryState {
  entries: AuditionHistoryEntry[];
  loading: boolean;
  error: boolean;
  hasMore: boolean;
  /** (Re)fetch the first page — call when the History tab opens. */
  load: () => void;
  /** Grow the window by one page. */
  loadMore: () => void;
  /** Optimistically insert a just-completed audition at the top. */
  prepend: (entry: AuditionHistoryEntry) => void;
}

export function useAuditionHistory(
  showcaseId: string | undefined,
): AuditionHistoryState {
  const { datoClientStore } = useStore();
  const [entries, setEntries] = useState<AuditionHistoryEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [limit, setLimit] = useState(AUDITION_HISTORY_PAGE_SIZE);
  const [hasMore, setHasMore] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Cleared on unmount so the fetch `finally` doesn't setState on an unmounted
  // component (abort leaves abortRef pointing at the in-flight controller).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const fetchPage = useCallback(
    async (nextLimit: number, trackOpen: boolean) => {
      if (!showcaseId) {
        setEntries([]);
        setHasMore(false);
        return;
      }
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setLoading(true);
      setError(false);
      try {
        const list = await datoClientStore.dubbingRepository.listVoiceTests({
          showcaseId,
          limit: nextLimit,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setEntries(list);
        // A full page back implies there may be more behind it.
        setHasMore(list.length >= nextLimit);
        if (trackOpen) {
          trackAiVoiceAuditionHistoryOpened({ count: list.length });
        }
      } catch (err) {
        if (isAbortError(err)) return;
        setError(true);
      } finally {
        if (abortRef.current === controller) {
          if (mountedRef.current) setLoading(false);
          abortRef.current = null;
        }
      }
    },
    [showcaseId, datoClientStore],
  );

  const load = useCallback(() => {
    setLimit(AUDITION_HISTORY_PAGE_SIZE);
    void fetchPage(AUDITION_HISTORY_PAGE_SIZE, true);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    const next = limit + AUDITION_HISTORY_PAGE_SIZE;
    setLimit(next);
    void fetchPage(next, false);
  }, [limit, fetchPage]);

  const prepend = useCallback((entry: AuditionHistoryEntry) => {
    setEntries(prev =>
      prev.some(e => e.auditionId === entry.auditionId)
        ? prev
        : [entry, ...prev],
    );
  }, []);

  // A workspace switch invalidates the current list; clear it and abort any
  // in-flight fetch so a late response can't land under the new workspace.
  useEffect(() => {
    setEntries([]);
    setHasMore(false);
    setLimit(AUDITION_HISTORY_PAGE_SIZE);
    setError(false);
    return () => abortRef.current?.abort();
  }, [showcaseId]);

  return { entries, loading, error, hasMore, load, loadMore, prepend };
}
